import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/common/Button';
import { Alert, AlertTitle, AlertDescription } from '@/components/common/Alert';
import api from '@/services/api';

interface LockerMonitorProps {
  lockerId: string;
  onStateChange?: (isClosed: boolean) => void;
}

const LockerMonitor: React.FC<LockerMonitorProps> = ({ lockerId, onStateChange }) => {
  const { t } = useTranslation();
  const [isLockerClosed, setIsLockerClosed] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  const [audioError, setAudioError] = useState<string>('');
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // Update parent component when locker state changes
  useEffect(() => {
    onStateChange?.(isLockerClosed);
    console.log('LockerMonitor state updated:', isLockerClosed); // Add logging
  }, [isLockerClosed, onStateChange]);

  // Poll locker state
  useEffect(() => {
    let isSubscribed = true;
    const pollInterval = 1000; // 1 second
    let timeoutId: number;

    const checkLockerState = async () => {
      if (!lockerId) return;
      
      try {
        const response = await api.get(`/locker/${lockerId}/state`);
        if (isSubscribed) {
          const closed = response.data.closed;
          console.log('API response - Locker state:', closed); // Add logging
          setIsLockerClosed(closed);
          setError('');
        }
      } catch (error) {
        if (isSubscribed) {
          setError(t('lockerStateError'));
          console.error('Error checking locker state:', error);
        }
      }

      if (isSubscribed) {
        timeoutId = window.setTimeout(checkLockerState, pollInterval);
      }
    };

    checkLockerState();

    return () => {
      isSubscribed = false;
      window.clearTimeout(timeoutId);
    };
  }, [lockerId, t]);

  // Rest of the component remains the same...
  
  return (
    <div className="space-y-4">
      <div className="text-center space-y-2">
        <div className={`text-xl font-bold ${isLockerClosed ? 'text-green-600' : 'text-red-600'}`}>
          {isLockerClosed ? t('lockerClosed') : t('lockerOpen')}
        </div>
        {!isLockerClosed && (
          <div className="text-sm text-gray-600">
            {t('pleaseCloseLocker')}
          </div>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

export default LockerMonitor;